import React, { useMemo } from 'react';
import { RouteProps } from 'react-router-dom';
import DashboardRoutes from 'src/investorSystem/routes/DashboardRoutes';
import { Investment, Offering, Operator } from 'src/types';
import {
  getInvestmentOperatorId,
  getInvestmentOperatorObject,
  getOrganizationName,
  isFundInvestment,
  isProjectInvestment,
} from 'src/utils/functions/investorServices';
import { removeDuplicatesArray } from 'src/utils/functions/removeDuplicatesFromArray';

const OperatorView = React.lazy(() => import('../../views/OperatorView/OperatorPage'));
const ProjectView = React.lazy(() => import('../../views/ProjectView/ProjectViewIndex'));

const Offerings = React.lazy(() => import('../../views/Offerings/Offerings'));
const FundView = React.lazy(() => import('../../views/FundView/FundView'));

//Updates views
const UpdatesView = React.lazy(() => import('../../views/AllUpdatesView/AllUpdates'));
const SingleUpdateView = React.lazy(() => import('../../views/SingleUpdate/SingleUpdate'));

const useDashboardRoutes = (
  investments: Investment[] | null,
  offerings: Offering[],
): RouteProps[] => {
  return useMemo(() => {
    if (investments) {
      const initDashboardRoutes = DashboardRoutes;
      const operatorsRoutes = getOperatorsRoutes(investments);
      const investmentsRoutes = getInvestmentsRoutes(investments);
      const offeringsRoutes = getOfferingsRoutes(offerings);

      //combine all routes
      const allRoutes = [
        ...initDashboardRoutes,
        ...operatorsRoutes,
        ...investmentsRoutes,
        ...offeringsRoutes,
      ];

      return allRoutes;
    } else {
      return [];
    }
  }, [investments, offerings]);

  function getOperatorsRoutes(investments: Investment[]): RouteProps[] {
    const operators: Operator[] = investments.reduce((acc, investment) => {
      const operator = getInvestmentOperatorObject(investment);
      return [...acc, operator];
    }, []);
    return removeDuplicatesArray(operators).map((operator: Operator) => ({
      path: `/${operator.id}`,
      name: operator?.name,
      component: OperatorView,
      exact: true,
    }));
  }

  function getInvestmentsRoutes(investments: Investment[]): RouteProps[] {
    return investments
      .flatMap(investment => {
        const operatorId = getInvestmentOperatorId(investment).toLowerCase();
        const organizationName = getOrganizationName(investment).toLowerCase();
        const component = isProjectInvestment(investment)
          ? ProjectView
          : isFundInvestment(investment)
          ? FundView
          : null;

        if (!component) return []; //if the component is null, skip this iteration

        const basePath = `/${operatorId}/${investment.id}`;
        return [
          {
            path: `/:operatorIdUrl/:investmentIdUrl/updates/:updateIdUrl`, // Route for viewing a specific update
            name: `${organizationName} Single Update`,
            component: SingleUpdateView, // Wrapper to view a single update
            exact: true,
          },
          {
            path: '/:operatorIdUrl/:investmentIdUrl/updates', // Route for listing all updates
            name: `${organizationName} Updates`,
            component: UpdatesView, // Wrapper to list all updates
            exact: true,
          },
          {
            path: basePath,
            name: organizationName,
            component,
          },
        ];
      })
      .filter(x => x);
  }

  /**
   * Returns the routes for the offerings
   * @param offerings Array of offerings
   * @returns Returns the routes for the offerings
   */
  function getOfferingsRoutes(offerings: Offering[]): RouteProps[] {
    return (
      offerings?.reduce((acc, offering) => {
        if (offering.operatorId) {
          acc.push({
            path: `/${offering.operatorId}/offerings`,
            name: 'offerings',
            component: Offerings,
          });
        }
        return acc;
      }, []) ?? []
    );
  }
};

export default useDashboardRoutes;
